import { useState, useCallback, useEffect, useRef } from 'react';
import { JSONSchema7 } from 'json-schema';
import { Entity } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { openChoreoClientApiRef } from '../../../api/OpenChoreoClientApi';
import { getSchemaDefaults } from '../overridesUtils';
import type { ModelsWorkload } from '@openchoreo/backstage-plugin-common';

/**
 * Tracks whether form sections have been initialized by RJSF.
 * RJSF triggers onChange on initial render with normalized data,
 * which can differ from our schema defaults. We capture the first
 * onChange as the "true" initial state to avoid false change detection.
 */
interface FormInitFlags {
  component: boolean;
  traits: Record<string, boolean>;
  workload: boolean;
}

interface ReleaseBinding {
  name: string;
  environment: string;
  componentTypeEnvironmentConfigs?: Record<string, unknown>;
  traitEnvironmentConfigs?: Record<string, Record<string, unknown>>;
  workloadOverrides?: {
    container?: any;
  };
}

export interface OverridesFormState {
  componentTypeFormData: Record<string, unknown>;
  traitFormDataMap: Record<string, Record<string, unknown>>;
  initialComponentTypeFormData: Record<string, unknown>;
  initialTraitFormDataMap: Record<string, Record<string, unknown>>;
  initialWorkloadFormData: Record<string, unknown>;
  workloadFormData: any;
  // Track whether actual overrides exist from backend (not just defaults)
  hasActualComponentOverrides: boolean;
  hasActualTraitEnvironmentConfigsMap: Record<string, boolean>;
  hasActualWorkloadOverrides: boolean;
  // Base workload data for reference (shows original env vars)
  baseWorkloadData: ModelsWorkload | null;
}

export interface OverridesSchemaState {
  componentTypeSchema: JSONSchema7 | null;
  traitSchemasMap: Record<string, JSONSchema7>;
}

interface UseOverridesDataReturn {
  loading: boolean;
  error: string | null;
  schemas: OverridesSchemaState;
  formState: OverridesFormState;
  expandedSections: Record<string, boolean>;
  setComponentTypeFormData: (data: Record<string, unknown>) => void;
  setTraitFormDataMap: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, unknown>>>
  >;
  setExpandedSections: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  setWorkloadFormData: (data: any) => void;
  reload: () => void;
}

// Check if a container has actual override data (not just empty arrays)
const hasContainerOverrideData = (container: any): boolean => {
  if (!container) return false;
  const hasEnvVars = Array.isArray(container.env) && container.env.length > 0;
  const hasFiles = Array.isArray(container.files) && container.files.length > 0;
  const hasImage = !!container.image;
  const hasCommand =
    Array.isArray(container.command) && container.command.length > 0;
  const hasArgs = Array.isArray(container.args) && container.args.length > 0;
  return hasEnvVars || hasFiles || hasImage || hasCommand || hasArgs;
};

// Check if workload has any actual override data
const hasActualWorkloadData = (workloadOverrides: any): boolean => {
  return hasContainerOverrideData(workloadOverrides?.container);
};

/**
 * Hook for loading and managing environment overrides data.
 * Fetches schema and existing bindings for an environment.
 */
export function useOverridesData(
  entity: Entity,
  environmentName: string | undefined,
  releaseName: string | undefined,
  isOpen: boolean,
): UseOverridesDataReturn {
  const client = useApi(openChoreoClientApiRef);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Schema state
  const [componentTypeSchema, setComponentTypeSchema] =
    useState<JSONSchema7 | null>(null);
  const [traitSchemasMap, setTraitSchemasMap] = useState<
    Record<string, JSONSchema7>
  >({});

  // Form data state
  const [componentTypeFormData, setComponentTypeFormData] = useState<
    Record<string, unknown>
  >({});
  const [traitFormDataMap, setTraitFormDataMap] = useState<
    Record<string, Record<string, unknown>>
  >({});
  const [initialComponentTypeFormData, setInitialComponentTypeFormData] =
    useState<Record<string, unknown>>({});
  const [initialTraitFormDataMap, setInitialTraitFormDataMap] = useState<
    Record<string, Record<string, unknown>>
  >({});
  const [workloadFormData, setWorkloadFormData] = useState<any>({});
  const [initialWorkloadFormData, setInitialWorkloadFormData] = useState<any>(
    {},
  );

  // Track whether actual overrides exist from backend (not just defaults)
  const [hasActualComponentOverrides, setHasActualComponentOverrides] =
    useState<boolean>(false);
  const [
    hasActualTraitEnvironmentConfigsMap,
    setHasActualTraitEnvironmentConfigsMap,
  ] = useState<Record<string, boolean>>({});
  const [hasActualWorkloadOverrides, setHasActualWorkloadOverrides] =
    useState<boolean>(false);

  // Base workload data for reference (shows original env vars)
  const [baseWorkloadData, setBaseWorkloadData] =
    useState<ModelsWorkload | null>(null);

  // Accordion state
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({ component: true, workload: false });

  // Track form initialization to handle RJSF's first onChange event.
  // RJSF normalizes data on initial render, so we capture that as the true initial state.
  const formInitFlags = useRef<FormInitFlags>({
    component: false,
    traits: {},
    workload: false,
  });

  // Create an empty ContainerOverride structure when the workload has a container.
  const createContainersFromWorkload = (workloadInfo: any) => {
    if (workloadInfo?.container) {
      return { container: { env: [], files: [] } };
    }
    // Base workload fetch failed (null) — fall back to an empty container structure
    // so the Workload tab remains editable even when base data is unavailable.
    if (workloadInfo === null) {
      return { container: { env: [], files: [] } };
    }
    // workloadInfo exists but has no container — no container structure needed.
    return null;
  };

  const loadSchemaAndBinding = useCallback(async () => {
    if (!releaseName) {
      setError('No release deployed to this environment');
      return;
    }

    setLoading(true);
    setError(null);

    // Reset form initialization flags on reload
    formInitFlags.current = {
      component: false,
      traits: {},
      workload: false,
    };

    try {
      // Fetch schema for the release
      const schemaResponse = await client.fetchComponentReleaseSchema(
        entity,
        releaseName,
      );

      // Store component type schema for later use
      let compTypeOverridesSchema: JSONSchema7 | null = null;

      if (schemaResponse.success && schemaResponse.data) {
        const wrappedSchema = schemaResponse.data as {
          properties?: {
            componentTypeEnvironmentConfigs?: JSONSchema7;
            traitEnvironmentConfigs?: {
              properties?: Record<string, JSONSchema7>;
            };
          };
        };
        const compTypeOverrides =
          wrappedSchema.properties?.componentTypeEnvironmentConfigs;
        const traitEnvironmentConfigs =
          wrappedSchema.properties?.traitEnvironmentConfigs;

        if (compTypeOverrides) {
          compTypeOverridesSchema = compTypeOverrides as JSONSchema7;
          setComponentTypeSchema(compTypeOverridesSchema);
        }

        if (traitEnvironmentConfigs?.properties) {
          const traitSchemas: Record<string, JSONSchema7> = {};
          Object.entries(traitEnvironmentConfigs.properties).forEach(
            ([traitName, schema]) => {
              traitSchemas[traitName] = schema as JSONSchema7;
            },
          );
          setTraitSchemasMap(traitSchemas);

          // Initialize expanded state for each trait
          const newExpandedSections: Record<string, boolean> = {
            component: true,
            workload: false,
          };
          Object.keys(traitSchemas).forEach(traitName => {
            newExpandedSections[`trait-${traitName}`] = false;
          });
          setExpandedSections(newExpandedSections);
        }
      } else {
        throw new Error('Failed to fetch schema');
      }

      // Fetch existing bindings to get current overrides
      const bindingsResponse = await client.fetchReleaseBindings(entity);

      if (bindingsResponse.success && bindingsResponse.data?.items) {
        const bindings = bindingsResponse.data.items as ReleaseBinding[];
        const currentBinding = bindings.find(
          b => b.environment?.toLowerCase() === environmentName?.toLowerCase(),
        );

        if (currentBinding) {
          const componentOverrides =
            currentBinding.componentTypeEnvironmentConfigs || {};

          // Track whether actual component overrides exist from backend
          const hasActualComponent = Object.keys(componentOverrides).length > 0;
          setHasActualComponentOverrides(hasActualComponent);

          // If no component overrides exist, use schema defaults
          // This ensures the form shows defaults and detects no changes
          if (!hasActualComponent) {
            const schemaDefaults = getSchemaDefaults(compTypeOverridesSchema);
            setComponentTypeFormData(schemaDefaults);
            setInitialComponentTypeFormData(schemaDefaults);
          } else {
            setComponentTypeFormData(componentOverrides);
            setInitialComponentTypeFormData(componentOverrides);
          }

          const existingTraitEnvironmentConfigs =
            currentBinding.traitEnvironmentConfigs || {};
          setTraitFormDataMap(existingTraitEnvironmentConfigs);
          setInitialTraitFormDataMap(existingTraitEnvironmentConfigs);

          // Track which traits have actual overrides from backend
          const traitEnvironmentConfigsMap: Record<string, boolean> = {};
          Object.keys(existingTraitEnvironmentConfigs).forEach(traitName => {
            traitEnvironmentConfigsMap[traitName] =
              Object.keys(existingTraitEnvironmentConfigs[traitName] || {})
                .length > 0;
          });
          setHasActualTraitEnvironmentConfigsMap(traitEnvironmentConfigsMap);

          const workloadOverrides = currentBinding.workloadOverrides || {};
          // Track whether actual workload overrides exist from backend
          // (not just empty container structures)
          const hasActualWorkload = hasActualWorkloadData(workloadOverrides);
          setHasActualWorkloadOverrides(hasActualWorkload);

          // Fetch base workload from component release (snapshotted workload at release time)
          const releaseResponse = await client.fetchComponentRelease(
            entity,
            releaseName!,
          );
          const workloadInfo = (releaseResponse?.data?.spec?.workload ??
            null) as ModelsWorkload | null;
          setBaseWorkloadData(workloadInfo);

          // If no workload overrides exist, populate container structure from base workload
          if (!hasActualWorkload) {
            const populatedWorkloadOverrides =
              createContainersFromWorkload(workloadInfo);
            if (populatedWorkloadOverrides) {
              setWorkloadFormData(populatedWorkloadOverrides);
              setInitialWorkloadFormData(populatedWorkloadOverrides);
            } else {
              setWorkloadFormData(workloadOverrides);
              setInitialWorkloadFormData(workloadOverrides);
            }
          } else {
            setWorkloadFormData(workloadOverrides);
            setInitialWorkloadFormData(workloadOverrides);
          }
        } else {
          // No binding exists - use schema defaults for initial form data
          const schemaDefaults = getSchemaDefaults(compTypeOverridesSchema);
          setComponentTypeFormData(schemaDefaults);
          setInitialComponentTypeFormData(schemaDefaults);
          setTraitFormDataMap({});
          setInitialTraitFormDataMap({});

          // Fetch base workload from component release (snapshotted workload at release time)
          const releaseResponse = await client.fetchComponentRelease(
            entity,
            releaseName!,
          );
          const workloadInfo = (releaseResponse?.data?.spec?.workload ??
            null) as ModelsWorkload | null;
          setBaseWorkloadData(workloadInfo);

          // Populate container structure from base workload
          const populatedWorkloadOverrides =
            createContainersFromWorkload(workloadInfo);
          if (populatedWorkloadOverrides) {
            setWorkloadFormData(populatedWorkloadOverrides);
            setInitialWorkloadFormData(populatedWorkloadOverrides);
          } else {
            setWorkloadFormData({});
            setInitialWorkloadFormData({});
          }

          // No actual overrides exist
          setHasActualComponentOverrides(false);
          setHasActualTraitEnvironmentConfigsMap({});
          setHasActualWorkloadOverrides(false);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [entity, client, environmentName, releaseName]);

  useEffect(() => {
    if (isOpen && environmentName) {
      loadSchemaAndBinding();
    }
  }, [isOpen, environmentName, loadSchemaAndBinding]);

  /**
   * Wrapper setter for component form data that handles RJSF initialization.
   * On the first call (from RJSF's initial render), captures the normalized
   * data as both initial and current state to prevent false change detection.
   */
  const handleSetComponentTypeFormData = useCallback(
    (data: Record<string, unknown>) => {
      // Only capture initial state if backend had no actual overrides
      // and this is the first onChange (RJSF initialization)
      if (!hasActualComponentOverrides && !formInitFlags.current.component) {
        formInitFlags.current.component = true;
        setInitialComponentTypeFormData(data);
        setComponentTypeFormData(data);
      } else {
        setComponentTypeFormData(data);
      }
    },
    [hasActualComponentOverrides],
  );

  /**
   * Wrapper setter for workload form data that handles RJSF initialization.
   */
  const handleSetWorkloadFormData = useCallback(
    (data: any) => {
      if (!hasActualWorkloadOverrides && !formInitFlags.current.workload) {
        formInitFlags.current.workload = true;
        setInitialWorkloadFormData(data);
        setWorkloadFormData(data);
      } else {
        setWorkloadFormData(data);
      }
    },
    [hasActualWorkloadOverrides],
  );

  return {
    loading,
    error,
    schemas: {
      componentTypeSchema,
      traitSchemasMap,
    },
    formState: {
      componentTypeFormData,
      traitFormDataMap,
      initialComponentTypeFormData,
      initialTraitFormDataMap,
      initialWorkloadFormData,
      workloadFormData,
      hasActualComponentOverrides,
      hasActualTraitEnvironmentConfigsMap,
      hasActualWorkloadOverrides,
      baseWorkloadData,
    },
    expandedSections,
    setComponentTypeFormData: handleSetComponentTypeFormData,
    setTraitFormDataMap,
    setExpandedSections,
    setWorkloadFormData: handleSetWorkloadFormData,
    reload: loadSchemaAndBinding,
  };
}
